import React, { useMemo, useState } from 'react'
import {
  SelectionBox,
  SelectionBoxGroup,
  SELECTION_BOX_PREVIEW_STATE_LABELS,
  SELECTION_BOX_PREVIEW_STATES,
  type SelectionBoxPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SelectionBoxShowcase.css'

const DEMO_OPTIONS = [
  {
    value: 'standard',
    title: 'Standard',
    description: 'Default plan for most clinics',
  },
  {
    value: 'priority',
    title: 'Priority',
    description: 'Faster support response times',
  },
  {
    value: 'enterprise',
    title: 'Enterprise',
    description: 'Custom SLAs and dedicated success',
  },
]

const SelectionBoxShowcase: React.FC = () => {
  const [title, setTitle] = useState('Title')
  const [description, setDescription] = useState('optional description')
  const [showDescription, setShowDescription] = useState(true)
  const [showSelectIndicator, setShowSelectIndicator] = useState(true)
  const [selected, setSelected] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [groupValue, setGroupValue] = useState('standard')
  const [highlightedState, setHighlightedState] =
    useState<SelectionBoxPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'selection-box-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="selection-box-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as SelectionBoxPreviewState,
                  )
                }
              >
                {SELECTION_BOX_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SELECTION_BOX_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'selection-box-title',
            label: 'Title',
            control: (
              <input
                id="selection-box-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'selection-box-description',
            label: 'Description',
            control: (
              <input
                id="selection-box-description"
                className="ui-field-control"
                type="text"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            ),
          },
          {
            id: 'selection-box-show-description',
            label: 'Show description',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selection-box-show-description"
                  type="checkbox"
                  checked={showDescription}
                  onChange={(event) => setShowDescription(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selection-box-show-indicator',
            label: 'Show Selected label',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selection-box-show-indicator"
                  type="checkbox"
                  checked={showSelectIndicator}
                  onChange={(event) =>
                    setShowSelectIndicator(event.target.checked)
                  }
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selection-box-selected',
            label: 'Selected',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selection-box-selected"
                  type="checkbox"
                  checked={selected}
                  onChange={(event) => setSelected(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selection-box-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selection-box-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      darkMode,
      description,
      highlightedState,
      selected,
      showDescription,
      showSelectIndicator,
      title,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Selection box"
        description="Radio-style option card with title, optional description, and a Selected label. Distinct from circular Radio and from Selectable card (Favourites/Bundles)."
        stageClassName="ui-preview__stage--wide selection-box-showcase__stage-wrap"
      >
        <div
          className={`selection-box-showcase__sheet${
            darkMode ? ' selection-box-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click to toggle selection. Use the properties panel to adjust copy
              and dark mode.
            </p>
            <SelectionBox
              title={title}
              description={description}
              showDescription={showDescription}
              showSelectIndicator={showSelectIndicator}
              selected={selected}
              darkMode={darkMode}
              onSelect={() => setSelected((current) => !current)}
            />
          </section>

          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Grouped (single-select)</h3>
            <p className="ui-showcase-live__note">
              SelectionBoxGroup enforces one active option, like RadioGroup.
            </p>
            <SelectionBoxGroup
              label="Plan"
              value={groupValue}
              onChange={setGroupValue}
              options={DEMO_OPTIONS}
              darkMode={darkMode}
            />
          </section>

          <div className="selection-box-showcase__section-header">
            <span className="selection-box-showcase__section-label">
              Variants
            </span>
          </div>

          <div className="selection-box-showcase__matrix">
            {SELECTION_BOX_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`selection-box-showcase__matrix-cell${
                  highlightedState === previewState
                    ? ' selection-box-showcase__matrix-cell--active'
                    : ''
                }${darkMode ? ' selection-box-showcase__matrix-cell--dark' : ''}`}
              >
                <span className="selection-box-showcase__matrix-label">
                  {SELECTION_BOX_PREVIEW_STATE_LABELS[previewState]}
                  {darkMode ? ' · Dark' : ' · Light'}
                </span>
                <SelectionBox
                  title="Title"
                  description="optional description"
                  previewState={previewState}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </div>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="SelectionBox"
        sections={propertySections}
      />
    </>
  )
}

export default SelectionBoxShowcase
